import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import client from "../lib/api";
import { Timer, Broadcast } from "@phosphor-icons/react";

/**
 * EtaEmbedPage — minimal iframe-friendly ETA card for brokers to embed
 * in their TMS dashboards.
 *
 * Route: /embed/eta/:loadId
 * Usage in a broker's TMS:
 *   <iframe src="https://freightpilotai.ai/embed/eta/LOAD_123"
 *           width="360" height="140"
 *           style="border:none;background:transparent"></iframe>
 *
 * The page auto-refreshes every 60s so the ETA stays live in the
 * broker's dashboard without any code on their side.
 */
export default function EtaEmbedPage() {
  const { loadId } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    async function tick() {
      try {
        const { data: d } = await client.get(`/public/eta/${loadId}`);
        if (alive) setData(d);
      } catch (e) {
        if (alive) setError(e?.response?.data?.detail || "ETA unavailable");
      }
    }
    tick();
    const iv = setInterval(tick, 60_000);
    return () => { alive = false; clearInterval(iv); };
  }, [loadId]);

  if (error) {
    return (
      <div className="min-h-[140px] p-4 rounded-lg border border-red-500/30 bg-[#0A0A0A] text-red-300 text-sm" data-testid="eta-embed-error">
        {error}
      </div>
    );
  }
  if (!data) {
    return <div className="min-h-[140px] p-4 rounded-lg bg-[#0A0A0A] text-neutral-500 text-sm" data-testid="eta-embed-loading">Loading ETA…</div>;
  }

  const eta = data.predicted_eta;
  const status = data.status;
  const isInMotion = ["arrived_pickup", "in_transit", "arrived_delivery"].includes(status);

  return (
    <div className="min-h-[140px] p-5 rounded-xl border border-white/10 bg-[#0A0A0A] text-white font-sans" data-testid="eta-embed-card" style={{fontFamily:"-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif"}}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
          <Timer size={16} weight="fill" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[9px] uppercase tracking-widest text-[#FF5A1F] font-bold">
            Load {data.load_number || loadId.slice(0, 8)}
          </div>
          <div className="text-sm font-medium text-white truncate">
            {data.origin || "?"} → {data.destination || "?"}
          </div>
        </div>
        {eta?.confidence_level && (
          <ConfidenceDot level={eta.confidence_level} />
        )}
      </div>

      {isInMotion && eta ? (
        <div>
          <div className="text-2xl font-bold text-white leading-tight">
            {formatCountdown(eta.minutes_remaining)}
          </div>
          <div className="text-[11px] text-neutral-400 mt-1 flex items-center gap-2">
            <span>ETA {new Date(eta.eta_iso).toLocaleString([], { weekday: "short", hour: "numeric", minute: "2-digit" })}</span>
            <span>·</span>
            <span>{Math.round(eta.distance_mi || 0)} mi</span>
            {eta.gps_freshness === "stale" && <><span>·</span><span className="text-amber-400">stale GPS</span></>}
          </div>
        </div>
      ) : (
        <div>
          <div className="text-lg font-medium text-neutral-300">
            {status ? status.replace(/_/g, " ") : "unknown"}
          </div>
          <div className="text-[11px] text-neutral-500 mt-1">
            {data.driver_first_name ? `Driver: ${data.driver_first_name}` : "No driver assigned yet"}
          </div>
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-white/5 flex items-center justify-between text-[9px] uppercase tracking-widest text-neutral-500">
        <span className="inline-flex items-center gap-1"><Broadcast size={9} /> Powered by FreightPilot AI</span>
        <span className="font-mono">v{data.api_version || "1"}</span>
      </div>
    </div>
  );
}

function formatCountdown(mins) {
  if (!mins || mins <= 0) return "Arriving now";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

function ConfidenceDot({ level }) {
  const c = {
    high: { cls: "bg-emerald-500", label: "HIGH" },
    med:  { cls: "bg-amber-400",  label: "MED" },
    low:  { cls: "bg-red-400",     label: "LOW" },
  }[level] || { cls: "bg-neutral-500", label: level.toUpperCase() };
  return (
    <div className="shrink-0 flex items-center gap-1" title={`Confidence: ${c.label}`} data-testid={`embed-confidence-${level}`}>
      <span className={`inline-block w-2 h-2 rounded-full ${c.cls}`} />
      <span className="text-[9px] uppercase tracking-widest text-neutral-500 font-bold">{c.label}</span>
    </div>
  );
}
